import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer,
} from 'recharts';
import { useMarketingStore } from '../../store/marketing.store';
import InfoTooltip from '../revenue/InfoTooltip';

type SortKey = 'pageName' | 'platform' | 'count';
type SortDir = 'asc' | 'desc';

function formatNum(value: number): string {
  return value.toLocaleString('en-US');
}

function platformLabel(p: string): string {
  const s = (p || '').toLowerCase();
  if (s.includes('facebook')) return 'Facebook';
  if (s.includes('instagram')) return 'Instagram';
  if (s.includes('tiktok')) return 'TikTok';
  if (s.includes('zalo')) return 'Zalo';
  return p || '—';
}

function PlatformIcon({ platform }: { platform: string }) {
  const s = (platform || '').toLowerCase();
  const size = 18;
  if (s.includes('facebook')) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877F2" aria-label="Facebook">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    );
  }
  if (s.includes('instagram')) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-label="Instagram">
        <defs>
          <linearGradient id="igGrad" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FEDA77"/>
            <stop offset="50%" stopColor="#F58529"/>
            <stop offset="75%" stopColor="#DD2A7B"/>
            <stop offset="100%" stopColor="#8134AF"/>
          </linearGradient>
        </defs>
        <path fill="url(#igGrad)" d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
      </svg>
    );
  }
  if (s.includes('tiktok')) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-label="TikTok">
        <path fill="#010101" d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.84-.1z"/>
        <path fill="#25F4EE" d="M17.81 8.28v-.66A4.84 4.84 0 0115 6.76a4.85 4.85 0 002.81 1.52z"/>
      </svg>
    );
  }
  if (s.includes('zalo')) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="#0068FF" aria-label="Zalo">
        <circle cx="12" cy="12" r="12"/>
        <text x="12" y="16" textAnchor="middle" fontSize="10" fill="white" fontWeight="700">Z</text>
      </svg>
    );
  }
  return <span className="inline-block w-[18px] h-[18px]" />;
}

const PANCAKE_SOURCE_NOTE = 'Nguồn: Pancake (pages.fm) — đếm conversation INBOX mới theo inserted_at (GMT+7). Chỉ tính INBOX, không tính comment. Mỗi khách chỉ tính 1 lần ở thời điểm inbox lần đầu, các tin nhắn tiếp theo trong cùng conversation không được đếm lại.';

export default function MessagingOverviewChart({ isDark }: { isDark: boolean }) {
  const messaging = useMarketingStore((s) => s.messaging);
  const [sortKey, setSortKey] = useState<SortKey>('count');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Page colors cycle — match order in sortedByPage (desc count)
  const PAGE_COLORS = ['#06b6d4', '#f59e0b', '#ec4899', '#8b5cf6', '#10b981', '#ef4444', '#3b82f6', '#eab308', '#14b8a6', '#f97316'];

  // Build pivoted daily data: each row = { date, [pageName1]: count, [pageName2]: count, ... }
  const stackedDaily = useMemo(() => {
    if (!messaging?.dailyByPage?.length) return { rows: [], series: [] };
    const dateMap = new Map<string, Record<string, any>>();
    const pageOrder: Array<{ id: string; name: string; total: number }> = [];
    const pageIdToName = new Map<string, string>();

    for (const r of messaging.dailyByPage) {
      if (!dateMap.has(r.date)) dateMap.set(r.date, { date: r.date });
      // Dedup page name (backend uses pageId as key, but multiple pages can share same display name
      // e.g. "25 FIT" on FB/IG/TikTok). Use "pageName (Platform)" for clarity when duplicates exist.
      if (!pageIdToName.has(r.pageId)) pageIdToName.set(r.pageId, r.pageName);
    }
    // Detect duplicate page names → append platform
    const nameCount = new Map<string, number>();
    for (const n of pageIdToName.values()) nameCount.set(n, (nameCount.get(n) || 0) + 1);
    const pageLabel = (_pageId: string, pageName: string, platform: string) =>
      (nameCount.get(pageName) || 0) > 1 ? `${pageName} (${platformLabel(platform)})` : pageName;

    // Sum per page (for ordering)
    const pageTotal = new Map<string, { name: string; total: number }>();
    for (const r of messaging.dailyByPage) {
      const label = pageLabel(r.pageId, r.pageName, r.platform);
      const ex = pageTotal.get(r.pageId);
      if (ex) ex.total += r.count;
      else pageTotal.set(r.pageId, { name: label, total: r.count });
      dateMap.get(r.date)![label] = (dateMap.get(r.date)![label] || 0) + r.count;
    }
    for (const [id, v] of pageTotal) pageOrder.push({ id, name: v.name, total: v.total });
    pageOrder.sort((a, b) => b.total - a.total);

    const rows = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    // Compute per-day total for label on top of stacked bar
    for (const r of rows) {
      r._total = Object.entries(r).reduce((s, [k, v]) => k !== 'date' && k !== '_total' ? s + (v as number) : s, 0);
    }
    const series = pageOrder.filter(p => p.total > 0).map((p, i) => ({
      name: p.name, color: PAGE_COLORS[i % PAGE_COLORS.length],
    }));
    return { rows, series };
  }, [messaging?.dailyByPage]);

  const sortedByPage = useMemo(() => {
    const rows = messaging?.byPage ? [...messaging.byPage] : [];
    const cmp = (a: any, b: any) => {
      const av = sortKey === 'platform' ? platformLabel(a.platform) : a[sortKey];
      const bv = sortKey === 'platform' ? platformLabel(b.platform) : b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv), 'vi');
    };
    rows.sort((a, b) => sortDir === 'asc' ? cmp(a, b) : -cmp(a, b));
    return rows;
  }, [messaging?.byPage, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'count' ? 'desc' : 'asc'); }
  };
  const sortArrow = (key: SortKey) => sortKey !== key ? ' ↕' : sortDir === 'asc' ? ' ↑' : ' ↓';

  const tooltipText = `Số tin nhắn mới (khách inbox lần đầu) trong kỳ.\n\n${PANCAKE_SOURCE_NOTE}`;

  if (!messaging || messaging.totalContacts === 0) {
    return null;
  }

  const cards = [
    { label: 'New Messages', value: formatNum(messaging.totalContacts), color: 'text-cyan-400', tooltip: `Số khách hàng inbox lần đầu trong kỳ — mỗi conversation INBOX trên Pancake được đếm 1 lần ở thời điểm tạo.\n\n${PANCAKE_SOURCE_NOTE}` },
  ];

  return (
    <div className={`backdrop-blur rounded-lg p-6 ${isDark ? 'bg-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Messages
        <InfoTooltip isDark={isDark} text={tooltipText} />
      </h3>

      {/* Scorecard */}
      <div className="mb-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-lg p-3 ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {c.label}
              {c.tooltip && <InfoTooltip isDark={isDark} text={c.tooltip} />}
            </p>
            <p className={`text-lg font-bold font-mono mt-1 ${isDark ? c.color : 'text-gray-900'}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Per-page breakdown table */}
      {messaging.byPage && messaging.byPage.length > 0 && (
        <div className={`mb-4 rounded-lg overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
          <div className={`px-3 py-2 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            New Messages theo Page (cách tính: thời gian tạo conversation trong khung thời gian chọn, GMT+7, chỉ INBOX, không tính comment)
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={isDark ? 'border-t border-white/10' : 'border-t border-gray-200'}>
                <th
                  onClick={() => toggleSort('pageName')}
                  className={`text-left px-2 sm:px-3 py-2 font-medium cursor-pointer select-none ${isDark ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >Page{sortArrow('pageName')}</th>
                <th
                  onClick={() => toggleSort('platform')}
                  className={`hidden sm:table-cell text-left px-3 py-2 font-medium cursor-pointer select-none ${isDark ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >Platform{sortArrow('platform')}</th>
                <th
                  onClick={() => toggleSort('count')}
                  className={`text-right px-2 sm:px-3 py-2 font-medium cursor-pointer select-none ${isDark ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >Messages{sortArrow('count')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedByPage.map((p) => (
                <tr key={p.pageId} className={isDark ? 'border-t border-white/10' : 'border-t border-gray-200'}>
                  <td className={`px-2 sm:px-3 py-2 ${isDark ? 'text-slate-200' : 'text-gray-800'}`}>
                    <span className="inline-flex items-center gap-2">
                      <PlatformIcon platform={p.platform} />
                      <span className="truncate max-w-[180px] sm:max-w-none inline-block align-middle">{p.pageName}</span>
                    </span>
                  </td>
                  <td className={`hidden sm:table-cell px-3 py-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{platformLabel(p.platform)}</td>
                  <td className={`px-2 sm:px-3 py-2 text-right font-mono ${isDark ? 'text-cyan-300' : 'text-gray-900'}`}>{formatNum(p.count)}</td>
                </tr>
              ))}
              <tr className={isDark ? 'border-t border-white/20 bg-white/5' : 'border-t border-gray-300 bg-gray-100'}>
                <td className={`px-2 sm:px-3 py-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} colSpan={2}>TỔNG</td>
                <td className={`px-2 sm:px-3 py-2 text-right font-mono font-semibold ${isDark ? 'text-cyan-300' : 'text-gray-900'}`}>
                  {formatNum(messaging.byPage.reduce((s, p) => s + p.count, 0))}
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      )}

      {stackedDaily.rows.length > 0 && (
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={stackedDaily.rows} margin={{ top: 24, right: 20, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e5e7eb'} />
            <XAxis
              dataKey="date"
              tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }}
              interval={stackedDaily.rows.length > 14 ? Math.ceil(stackedDaily.rows.length / 14) : 0}
            />
            <YAxis tick={{ fill: isDark ? '#94a3b8' : '#6b7280', fontSize: 11 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
                borderRadius: '8px',
                color: isDark ? '#f8fafc' : '#111827',
              }}
              formatter={(value: any) => formatNum(value as number)}
              labelStyle={{ color: isDark ? '#f8fafc' : '#111827', fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {stackedDaily.series.map((s, idx) => (
              <Bar
                key={s.name}
                dataKey={s.name}
                stackId="1"
                fill={s.color}
                isAnimationActive={false}
              >
                {idx === stackedDaily.series.length - 1 && (
                  <LabelList
                    dataKey="_total"
                    position="top"
                    formatter={(v: any) => formatNum(v as number)}
                    style={{ fill: isDark ? '#f8fafc' : '#111827', fontSize: 11, fontWeight: 600 }}
                  />
                )}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
